import { useState } from "react"
import { Link } from "@tanstack/react-router"
import {
  ArrowRight,
  BookOpen,
  Bot,
  Check,
  Copy,
  Globe,
  HelpCircle,
  MapPin,
  MessageSquare,
  Plane,
  Plug,
  Plus,
  ShieldCheck,
  Shuffle,
  Sparkles,
  Terminal,
  Wallet,
} from "lucide-react"
import { motion } from "motion/react"
import { Button } from "@/components/ui/button"
import { CryingChic } from "@/components/CryingChic"
import { SITE_URL } from "@/lib/seo"

// The Streamable HTTP endpoint our remote MCP server is served at — see
// src/routes/api/mcp/$.ts. This is the URL users paste into Claude's custom
// connector setup.
const MCP_URL = `${SITE_URL}/api/mcp`

// Everything here maps to a real tool the MCP server registers (see
// src/routes/api/mcp/$.ts + src/lib/ai/tools/) — if it's listed, Claude can do it.
const capabilities = [
  {
    icon: Plus,
    title: `Start a trip from one sentence`,
    body: `"Plan me 5 days in Lisbon in October." Claude resolves the destination to a real place, creates the trip, and sets up the day slots — you open ChicTravel and the skeleton is already there.`,
  },
  {
    icon: MapPin,
    title: `Add places that are actually places`,
    body: `When Claude adds a restaurant or a museum, it gets looked up properly — photo, address, pin on your trip map. Not a bullet list you have to re-type into a planner later.`,
  },
  {
    icon: Shuffle,
    title: `Reshuffle days like a local would`,
    body: `Move that overbooked Tuesday around: Claude can reorder stops within a day, shift things to other days, and regroup your plan by neighborhood so you stop crisscrossing the city.`,
  },
  {
    icon: Plane,
    title: `Log flights and hotels`,
    body: `Paste your flight details into the chat and they land on the itinerary with the right times. Hotels show up as a banner across every night of the stay, photo included.`,
  },
  {
    icon: Wallet,
    title: `Keep the budget honest`,
    body: `Set a trip budget, log expenses as they happen ("that dinner was €68"), and ask what's left. Claude reads the same budget your travel companions see in the app.`,
  },
  {
    icon: MessageSquare,
    title: `Notes, times, and transport`,
    body: `Drop a reminder on day 2 to book the Sagrada Família tickets, give the food tour a 10am start, or switch a leg from driving to walking. The small edits, handled in passing.`,
  },
  {
    icon: BookOpen,
    title: `Read everything it touches`,
    body: `Ask "what trips do I have coming up?" or "what does day 3 in Kyoto look like?" — Claude pulls the full day-by-day plan, places, notes, and order, then reasons about it.`,
  },
  {
    icon: Globe,
    title: `Check the web mid-plan`,
    body: `Opening hours, whether that ferry runs in winter, what's near your hotel — Claude can run a web search in the middle of planning without you switching tabs.`,
  },
]

// Real prompts a traveler can paste into Claude once connected. Kept concrete
// and scoped to what the MCP tools actually support today.
const scenarios = [
  {
    place: `Tokyo`,
    prompt: `Build me a 4-day Tokyo trip focused on food and neighborhoods I can walk.`,
  },
  {
    place: `Kyoto`,
    prompt: `Day 3 of my Kyoto trip looks overstuffed — move two things to day 4 and add a lunch spot near Arashiyama.`,
  },
  {
    place: `Barcelona`,
    prompt: `Look at my Barcelona trip and add notes for the best time to visit each spot.`,
  },
  {
    place: `Flights`,
    prompt: `Add my flight: SFO to Tokyo on June 12, departs 11:05am, lands 2:25pm the next day.`,
  },
  {
    place: `Budget`,
    prompt: `Set my Lisbon budget to $1,800 and log the $240 I just spent on the flight.`,
  },
  {
    place: `Bali`,
    prompt: `Create a relaxed 7-day Bali itinerary — beaches first half, culture second.`,
  },
  {
    place: `Planning ahead`,
    prompt: `What trips do I have on the books, and which ones still have empty days?`,
  },
  {
    place: `Rome`,
    prompt: `Add a note to day 1 of my Rome trip to pre-book the Colosseum underground tour.`,
  },
  {
    place: `Weekend`,
    prompt: `Make me a 2-day Portland weekend with coffee, bookstores, and one hike.`,
  },
]

const claudeAppSteps = [
  {
    number: `01`,
    title: `Open Claude's connector settings`,
    body: `In Claude (the desktop app or claude.ai), go to Settings → Connectors and choose "Add custom connector."`,
  },
  {
    number: `02`,
    title: `Paste your ChicTravel URL`,
    body: `Drop in the connector URL below. Claude walks you through a quick sign-in so it acts as you — and only you.`,
  },
  {
    number: `03`,
    title: `Start planning out loud`,
    body: `Ask Claude to create a trip or tweak an itinerary. Edits show up live in ChicTravel — even for collaborators watching.`,
  },
]

// Q&A copy is rendered on the page AND emitted as FAQPage JSON-LD below, so
// keep answers self-contained plain text (no JSX) to avoid the two drifting.
const faqs = [
  {
    q: `What is MCP, in plain words?`,
    a: `MCP (Model Context Protocol) is an open standard for plugging tools into AI assistants — think of it as a USB port for AI. ChicTravel runs an MCP server at ${MCP_URL}, and when you connect it, Claude gains a set of travel tools: create a trip, read an itinerary, add a place, log an expense. Claude decides when to use them based on what you ask; you just talk.`,
  },
  {
    q: `Which Claude plans support custom connectors?`,
    a: `Custom connectors are a feature of Claude's paid plans. The quick check: open Claude's settings — if you see a "Connectors" section with an "Add custom connector" option, you're set. Claude Code (the terminal app) supports MCP servers on any plan that includes it.`,
  },
  {
    q: `Does this work with Claude Code?`,
    a: `Yes. Run "claude mcp add --transport http chictravel ${MCP_URL}" in your terminal, then type /mcp inside Claude Code to sign in to ChicTravel. After that you can plan trips from the same terminal you work in — handy when a trip idea strikes mid-commit.`,
  },
  {
    q: `What can Claude actually see and touch?`,
    a: `Exactly what you can: the trips you own or collaborate on, nothing else. The connector signs in as you through ChicTravel's standard sign-in, and every action is checked against your account on our side. Disconnect it from Claude's settings at any time and access ends immediately.`,
  },
  {
    q: `Can Claude wreck my itinerary?`,
    a: `Edits apply to your real trip, so Claude can absolutely move your museum day — that's the point. But destructive actions are guarded: deleting an entire trip requires Claude to explicitly confirm you asked for it, and every change shows up live in the app, so you can watch what it's doing as it works.`,
  },
  {
    q: `How is this different from Chic, the assistant inside ChicTravel?`,
    a: `Same toolbox, different doorway. Chic lives inside the app and is the best way to plan when you're looking at the map. The Claude connector is for when you're already in a Claude conversation — researching a destination, say — and want the plan to land in a real itinerary instead of a wall of chat text.`,
  },
  {
    q: `Does the connector cost anything?`,
    a: `Not from us. It works with a free ChicTravel account — no credit card, no Pro requirement. You only need a Claude plan that supports connectors on the Anthropic side.`,
  },
  {
    q: `I use a different AI assistant. Am I out of luck?`,
    a: `Less than you'd think. MCP is an open standard, so any MCP-capable client can connect to the same URL. And ChicTravel Pro can export your full trip context as a file you can hand to any AI tool you like.`,
  },
]

function CopyableUrl() {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(MCP_URL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be blocked (insecure context / permissions); the URL is
      // still visible to copy by hand, so failing silently is fine.
    }
  }

  return (
    <div className="flex items-center gap-2 w-full max-w-md rounded-xl border border-border bg-background px-4 py-3">
      <Plug className="h-4 w-4 text-primary shrink-0" />
      <code className="text-sm font-mono truncate flex-1">{MCP_URL}</code>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="rounded-lg gap-1.5 shrink-0"
        onClick={handleCopy}
        aria-label="Copy connector URL"
      >
        {copied ? (
          <Check className="h-4 w-4 text-primary" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
        {copied ? `Copied` : `Copy`}
      </Button>
    </div>
  )
}

export function ClaudePage() {
  const faqJsonLd = {
    "@context": `https://schema.org`,
    "@type": `FAQPage`,
    mainEntity: faqs.map((f) => ({
      "@type": `Question`,
      name: f.q,
      acceptedAnswer: { "@type": `Answer`, text: f.a },
    })),
  }

  return (
    <main className="min-h-screen bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-12 md:pt-16 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <motion.div
            className="flex flex-col gap-5"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 w-fit rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              <Bot className="h-3.5 w-3.5 text-primary" />
              ChicTravel × Claude
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.08] tracking-tight">
              Plan smarter trips
              <br />
              <span className="text-primary">just by talking to Claude.</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-md">
              Connect ChicTravel to Claude Desktop, claude.ai, or Claude Code
              over MCP, and the conversation becomes the planner. Build
              itineraries, log flights and hotels, track the budget, reshuffle
              your days — every edit lands live in your real trip.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                size="lg"
                className="rounded-full px-8 h-12 text-base font-semibold shadow-md gap-2"
                asChild
              >
                <a href="#connect">
                  Connect to Claude <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-12 text-base font-medium"
                asChild
              >
                <Link to="/sign-up">Start free</Link>
              </Button>
            </div>
          </motion.div>

          {/* Example chat card */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-2xl border border-border bg-card shadow-2xl p-5 flex flex-col gap-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Bot className="h-4 w-4 text-primary" />
                Claude
              </div>
              <div className="self-end max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground">
                Reorganize my Tokyo trip by neighborhood so I'm not zig-zagging
                across the city each day.
              </div>
              <div className="self-start max-w-[90%] rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5 text-sm leading-relaxed">
                Done — I grouped day 1 around Shibuya & Harajuku, day 2 in Asakusa
                & Ueno, and day 3 across Shinjuku. I left notes on each day with
                the walking order. Want me to add lunch spots?
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Updated <span className="font-semibold text-foreground">Tokyo 2026</span>{` `}
                in ChicTravel
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── But what about Chic?? (comic relief) ──────────────────────── */}
      <section className="px-6 py-14 md:py-16 bg-primary/5 border-y border-border">
        <motion.div
          className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-8 sm:gap-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: `-60px` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="shrink-0">
            <CryingChic className="w-36 sm:w-44" />
            <p className="text-center text-xs text-muted-foreground mt-2 italic">
              Chic, taking the news well.
            </p>
          </div>
          <div className="text-center sm:text-left">
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-3">
              Wait… you're replacing Chic with Claude?!
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Relax — nobody's abandoning our beloved bird. Chic still runs the
              whole show <span className="font-semibold text-foreground">inside</span>{` `}
              ChicTravel. Claude just gets to help from the outside, like a very
              capable house guest.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              They're learning to share an itinerary. It's going… mostly fine.
              Chic's only cried twice today.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── What's actually happening (MCP explainer) ─────────────────── */}
      <section className="px-6 py-14 md:py-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
            The boring-but-useful part
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-5">
            What's actually happening here?
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              ChicTravel runs an{` `}
              <span className="font-semibold text-foreground">MCP server</span> —
              MCP being the Model Context Protocol, the open standard Anthropic
              published for connecting AI assistants to outside tools. The
              easiest way to picture it: a USB port for AI. You plug ChicTravel
              into Claude once, and Claude gains a set of real travel tools it
              can use whenever the conversation calls for them.
            </p>
            <p>
              Those tools aren't a separate "integration" we maintain on the
              side. They're the exact same ones Chic, our in-app assistant, uses
              to build trips every day — creating itineraries, looking up
              places, moving things between days, logging expenses. Claude gets
              the same toolbox, pointed at the same trips, with the same
              permission checks.
            </p>
            <p>
              The practical upshot: when you ask Claude to plan five days in
              Kyoto, you don't get a chat transcript to copy-paste somewhere.
              You get an actual trip in your ChicTravel account, with real
              places pinned on a real map, that your travel companions can open
              and start poking at immediately.
            </p>
          </div>
        </div>
      </section>

      {/* ── Capabilities ──────────────────────────────────────────────── */}
      <section className="px-6 py-14 md:py-16 bg-card border-y border-border">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              What Claude can do
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">
              Your itinerary, one message away
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Everything below works today, from Claude Desktop, claude.ai, or
              Claude Code.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {capabilities.map((c, i) => (
              <motion.div
                key={c.title}
                className="rounded-2xl border border-border bg-background p-6 flex gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: `-60px` }}
                transition={{
                  duration: 0.5,
                  delay: (i % 2) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <c.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{c.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── See it in action ──────────────────────────────────────────── */}
      <section id="see-it-in-action" className="px-6 py-16 md:py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              See it in action
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">
              Just say what you want
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Real things you can ask Claude once ChicTravel is connected. Copy
              one, tweak it, and watch your trip update.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenarios.map((s, i) => (
              <motion.div
                key={s.prompt}
                className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: `-60px` }}
                transition={{
                  duration: 0.5,
                  delay: (i % 3) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
                  <MapPin className="h-3.5 w-3.5" />
                  {s.place}
                </div>
                <p className="text-sm leading-relaxed">"{s.prompt}"</p>
              </motion.div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center max-w-2xl mx-auto mt-8 leading-relaxed">
            One tip from our own use: be concrete about days. "Move the temple
            to day 3" beats "move the temple to later." Claude reads your
            itinerary before editing, so naming a day gets you exactly what you
            meant on the first try.
          </p>
        </div>
      </section>

      {/* ── Connect ───────────────────────────────────────────────────── */}
      <section id="connect" className="px-6 py-16 md:py-20 bg-card border-y border-border scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
              Setup
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
              Connect ChicTravel to Claude
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              It takes about a minute either way. You'll need a ChicTravel
              account (free is fine) and a Claude plan that supports custom
              connectors. This is the connector URL for both paths:
            </p>
            <CopyableUrl />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Claude Desktop & claude.ai */}
            <div className="rounded-2xl border border-border bg-background p-7">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Bot className="h-4.5 w-4.5" />
                </div>
                <h3 className="font-bold text-lg">Claude Desktop & claude.ai</h3>
              </div>
              <div className="flex flex-col gap-6">
                {claudeAppSteps.map((s) => (
                  <div key={s.number} className="flex gap-5 items-start">
                    <span className="text-3xl font-black text-primary/20 leading-none w-10 shrink-0">
                      {s.number}
                    </span>
                    <div>
                      <p className="font-bold text-base mb-1">{s.title}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {s.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Claude Code */}
            <div className="rounded-2xl border border-border bg-background p-7">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Terminal className="h-4.5 w-4.5" />
                </div>
                <h3 className="font-bold text-lg">Claude Code</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                If you live in the terminal, you don't have to leave it. Add
                ChicTravel as an MCP server with one command:
              </p>
              <pre className="rounded-xl border border-border bg-muted/50 px-4 py-3 text-xs font-mono overflow-x-auto mb-4">
                <code>{`claude mcp add --transport http chictravel ${MCP_URL}`}</code>
              </pre>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Then run <code className="font-mono text-foreground">/mcp</code>{` `}
                inside Claude Code to sign in to your ChicTravel account. That's
                the whole setup.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                From there, "plan me a long weekend in Mexico City" works
                between builds. We won't judge what your commit history looks
                like the week before a vacation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Your data, your rules ─────────────────────────────────────── */}
      <section className="px-6 py-16 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
            Your data, your rules
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Claude signs in as you and only ever sees the trips you own or
            collaborate on — the exact same access you have in the app. Every
            action runs through the same permission checks as a click in
            ChicTravel; there's no side door. Nothing is shared with other
            users, and you can disconnect the connector from Claude at any time
            to cut off access instantly.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            And because edits show up in the app in real time, you're never
            wondering what the AI did — you can literally watch the places land
            on the map while Claude works.
          </p>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────── */}
      <section className="px-6 py-16 md:py-20 bg-card border-y border-border">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <HelpCircle className="h-5 w-5" />
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Questions we keep getting
            </h2>
          </div>
          <div className="divide-y divide-border">
            {faqs.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-bold text-base md:text-lg [&::-webkit-details-marker]:hidden">
                  {f.q}
                  <span className="text-primary text-xl leading-none mt-0.5 transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="text-muted-foreground leading-relaxed mt-3 pr-8">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-8">
            Curious how the connector stacks up in practice? We wrote a{` `}
            <Link
              to="/blogs/$slug"
              params={{ slug: `plan-trips-with-claude-mcp` }}
              className="text-primary font-medium hover:underline"
            >
              full walkthrough of planning a trip with Claude
            </Link>{` `}
            on the blog.
          </p>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-primary">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: `-60px` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-primary-foreground mb-4">
            Bring Claude along for the planning
          </h2>
          <p className="text-primary-foreground/80 text-base mb-10">
            Create a free ChicTravel account, connect Claude, and plan your next
            trip in conversation.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-8 h-12 font-semibold shadow-md gap-2 bg-white text-primary hover:bg-white/90"
              asChild
            >
              <Link to="/sign-up">
                Start planning free <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
          <p className="text-primary-foreground/60 text-xs mt-4">
            No credit card required
          </p>
        </motion.div>
      </section>
    </main>
  )
}
